/**
 * Preview Service Factory
 *
 * Centralized factory for creating and accessing preview generation services
 */

import { apiPreviewService } from './APIPreviewService';
import { powerQueryPreviewService } from './PowerQueryPreviewService';
import { pythonPreviewService } from './PythonPreviewService';
import { snowflakePreviewService } from './SnowflakePreviewService';

import type { APIPreviewService } from './APIPreviewService';
import type { PowerQueryPreviewService } from './PowerQueryPreviewService';
import type { PythonPreviewService } from './PythonPreviewService';
import type { SnowflakePreviewService } from './SnowflakePreviewService';

/**
 * Available preview service types
 */
export type PreviewServiceType = 'python' | 'powerQuery' | 'api' | 'curl' | 'snowflake';

/**
 * Factory for creating and managing preview services
 */
export class PreviewServiceFactory {
  private static instance: PreviewServiceFactory;

  // Service instances
  private readonly services = {
    python: pythonPreviewService,
    powerQuery: powerQueryPreviewService,
    api: apiPreviewService,
    snowflake: snowflakePreviewService
  };

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton factory instance
   */
  static getInstance(): PreviewServiceFactory {
    if (!PreviewServiceFactory.instance) {
      PreviewServiceFactory.instance = new PreviewServiceFactory();
    }
    return PreviewServiceFactory.instance;
  }

  /**
   * Get a specific preview service
   */
  getService(
    type: PreviewServiceType
  ): APIPreviewService | PowerQueryPreviewService | PythonPreviewService | SnowflakePreviewService {
    switch (type) {
      case 'python':
        return this.services.python;
      case 'powerQuery':
        return this.services.powerQuery;
      case 'api':
      case 'curl':
        return this.services.api;
      case 'snowflake':
        return this.services.snowflake;
      default:
        throw new Error(`Unknown preview service type: ${type}`);
    }
  }

  /**
   * Get Python preview service
   */
  getPythonService(): PythonPreviewService {
    return this.services.python;
  }

  /**
   * Get Power Query preview service
   */
  getPowerQueryService(): PowerQueryPreviewService {
    return this.services.powerQuery;
  }

  /**
   * Get API preview service
   */
  getAPIService(): APIPreviewService {
    return this.services.api;
  }

  /**
   * Get Snowflake preview service
   */
  getSnowflakeService(): SnowflakePreviewService {
    return this.services.snowflake;
  }
}

// Export singleton instance
export const previewServiceFactory = PreviewServiceFactory.getInstance();
